import { ACTIONS, EVENTS, STATUS } from 'react-joyride';

/**
 * As decisões do tutorial, fora do componente (MV2-1791).
 *
 * O react-joyride entrega um único `callback` com `action`, `type` e `status` em texto, e a
 * regra de "avançar, voltar ou encerrar" sai da combinação dos três. Aqui como função pura
 * porque montar o Joyride em teste exigiria @testing-library, que o repo não tem — e o que
 * quebra num tutorial controlado é justamente esta combinação.
 */

export type TourMove =
  /** Mostrar o passo `index` — depois de garantir que o alvo dele existe. */
  | { kind: 'go'; index: number }
  /** Fim do roteiro: marcar como visto e fechar. */
  | { kind: 'end' }
  /** Evento que não muda nada (posicionou, abriu o balão, etc.). */
  | { kind: 'stay' };

/**
 * Se o evento encerra o tutorial.
 *
 * Três saídas distintas: concluir no último passo (`FINISHED`), o botão Pular (`SKIPPED`) e o
 * X do balão (`ACTIONS.CLOSE`). O X não vem com status de fim — vem como `close` num
 * `step:after`, e sem tratá-lo à parte o tutorial reabria no passo seguinte em vez de fechar.
 */
export const endsTour = (action: string, status: string): boolean =>
  action === ACTIONS.CLOSE || status === STATUS.FINISHED || status === STATUS.SKIPPED;

/**
 * O que fazer com um evento do Joyride.
 *
 * `TARGET_NOT_FOUND` avança como um passo normal de propósito: se o alvo não montou mesmo
 * depois da espera, ficar parado deixaria a pessoa presa num balão sem âncora e sem botão que
 * funcione. Pular o passo perde um texto; travar perde o tutorial.
 */
export const moveFor = (
  { action, index, status, type }: { action: string; index: number; status: string; type: string },
  total: number,
): TourMove => {
  if (endsTour(action, status)) {
    return { kind: 'end' };
  }

  if (type !== EVENTS.STEP_AFTER && type !== EVENTS.TARGET_NOT_FOUND) {
    return { kind: 'stay' };
  }

  const next = index + (action === ACTIONS.PREV ? -1 : 1);

  // Voltar no primeiro passo não fecha o tutorial: o Joyride esconde o botão Voltar ali, mas
  // a seta do teclado ainda emite o evento.
  if (next < 0) {
    return { kind: 'go', index: 0 };
  }

  return next >= total ? { kind: 'end' } : { kind: 'go', index: next };
};

/**
 * Se o alvo de um passo já pode receber o balão.
 *
 * Existir no DOM não basta. O antd mantém montada a `TabPane` de toda aba já visitada e só a
 * esconde com `display: none`, então o alvo da aba anterior continua sendo encontrado por
 * `querySelector` — com caixa zerada. Posicionar nele põe o balão no canto superior esquerdo
 * da tela por um quadro, que é exatamente o "piscar no lugar errado" que o critério 5 proíbe.
 */
export const targetReady = (element: Element | null): boolean => {
  if (!element) return false;

  const box = element.getBoundingClientRect();

  return box.width > 0 && box.height > 0;
};
